import React from 'react';
import wingitDesign from '../designSystem';
import {
  getFlightFlowProgressState,
  getFlowState,
} from './flightFlowState';
import {
  CalendarPlus,
  ClipboardCheck,
  ClipboardPen,
  BookOpen,
} from 'lucide-react';

export default function DashboardFlightFlow({
  isMobile,
  membershipTier,
  workflowBooking,
  isPreFlightCompleted,
  preFlightProgressState,
  navigate,
  onBookAircraft,
}) {
  const styles = getStyles(isMobile);
  const flowState = getFlowState(workflowBooking);
  const hasBooking = Boolean(workflowBooking);
  const preFlightCompleted =
    isPreFlightCompleted === true;
  const resolvedPreFlightState =
    preFlightProgressState ||
    (preFlightCompleted
      ? 'completed'
      : 'not_started');

  const flowProgress =
    getFlightFlowProgressState({
      booking: workflowBooking,
      membershipTier,
      preFlightProgressState:
        resolvedPreFlightState,
      preflightComplete:
        preFlightCompleted,
      weightBalanceComplete:
        false,
      liveFlightActive:
        flowState === 'in_progress',
      closeFlightComplete: false,
      logbookComplete:
        undefined,
    });
  const logbookAvailable =
    flowProgress?.logbookAvailable !== false;

  const stageById = (
    flowProgress?.stages || []
  ).reduce((map, stage) => {
    map[stage.id] = stage;
    return map;
  }, {});

  const closeFlightStage =
    stageById['close-flight'] || null;
  const logbookStage =
    stageById.logbook || null;

  const postFlightClosed =
    flowState === 'post_complete';

  const logbookReminderActive =
    postFlightClosed &&
    logbookAvailable &&
    logbookStage?.status === 'current';

  const displayedFlowState =
    postFlightClosed ? 'none' : flowState;

  const displayedHasBooking =
    hasBooking && !postFlightClosed;
  const displayedCloseFlightStage =
    postFlightClosed ? null : closeFlightStage;

  const displayedLogbookStage =
    postFlightClosed && !logbookReminderActive
      ? null
      : logbookStage;

  function navigateToPreFlight() {
    const bookingId = String(
      workflowBooking?.id || ''
    ).trim();

    if (bookingId) {
      navigate(
        `/pre-flight?bookingId=${encodeURIComponent(bookingId)}`
      );
      return;
    }

    navigate('/pre-flight');
  }

  function navigateToPilotLogbook() {
    const bookingId = String(
      workflowBooking?.id || ''
    ).trim();

    if (bookingId) {
      navigate(
        `/pilot-logbook?bookingId=${encodeURIComponent(bookingId)}`
      );
      return;
    }

    navigate('/pilot-logbook');
  }

  const preFlightClickable =
    displayedHasBooking &&
    displayedFlowState !== 'landed' &&
    resolvedPreFlightState !== 'completed';

  const steps = [
    {
      icon: CalendarPlus,
      label: 'Book Flight',
      status: displayedHasBooking ? 'BOOKED' : 'Next step',
      tone: displayedHasBooking ? 'complete' : 'active',
      clickable: !displayedHasBooking,
      onClick: () => {
        if (onBookAircraft) {
          onBookAircraft();
          return;
        }

        navigate('/book-aircraft');
      },
      secondaryAction: displayedHasBooking
        ? {
            label: 'Amend Booking',
            onClick: () => {
              if (onBookAircraft) {
                onBookAircraft(workflowBooking?.id);
                return;
              }

              navigate('/book-aircraft');
            },
          }
        : null,
    },
    {
      icon: ClipboardCheck,
      label: 'Pre-Flight',
      status: getPreFlightStatus(
        resolvedPreFlightState,
        displayedFlowState
      ),
      tone: getPreFlightTone(
        resolvedPreFlightState,
        displayedFlowState
      ),
      helperLine:
        displayedHasBooking &&
        resolvedPreFlightState === 'action_required'
          ? 'Complete outstanding checks'
          : '',

      clickable: preFlightClickable,
      onClick: navigateToPreFlight,
      secondaryAction:
        displayedHasBooking &&
        !preFlightClickable
          ? {
              label: 'Review Pre-Flight',
              onClick: navigateToPreFlight,
            }
          : null,
    },
    {
      icon: ClipboardPen,
      label: 'Post Flight',
      status: getPostFlightStatus(
        displayedCloseFlightStage,
        displayedFlowState
      ),
      tone: getPostFlightTone(
        displayedCloseFlightStage,
        displayedFlowState
      ),
      clickable:
        displayedCloseFlightStage?.status ===
        'current',
      onClick: () => navigate('/my-bookings'),
    },
    {
      icon: BookOpen,
      label: 'Pilot Logbook',
      status: logbookAvailable
        ? getLogbookStatus(displayedLogbookStage)
        : 'Upgrade required',
      tone:
        logbookAvailable &&
        ['current', 'complete'].includes(
          displayedLogbookStage?.status
        )
          ? getLogbookTone(displayedLogbookStage)
          : 'locked',
      clickable:
        logbookAvailable &&
        ['current', 'complete'].includes(
          displayedLogbookStage?.status
        ),
      attentionPulse: logbookReminderActive,
      secondaryAction:
        logbookAvailable &&
        !['current', 'complete'].includes(
          displayedLogbookStage?.status
        )
          ? {
              label: 'Open Logbook',
              onClick: navigateToPilotLogbook,
            }
          : null,
      onClick: navigateToPilotLogbook,
    },
  ];

  return (
    <section style={styles.panel}>
      <style>
        {`
          @keyframes wingitLogbookReminderPulse {
            0%,
            100% {
              transform: scale(1);
              box-shadow: 0 0 0 rgba(85, 199, 255, 0);
            }

            50% {
              transform: scale(1.012);
              box-shadow: 0 0 26px rgba(85, 199, 255, 0.28);
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .wingit-logbook-reminder {
              animation: none !important;
            }
          }
        `}
      </style>

      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Flight flow</h2>
          <p style={styles.subtitle}>The next action in your flying workflow.</p>
        </div>
      </div>

      <div style={styles.flow}>
        {steps.map((step, index) => {
          const Icon = step.icon;
          const tone = getTone(step.tone);
          const Wrapper = step.clickable ? 'button' : 'div';
          const nextStep = steps[index + 1];
          const connectorTone = getConnectorTone(step, nextStep);

          return (
            <React.Fragment key={step.label}>
              <Wrapper
                type={step.clickable ? 'button' : undefined}
                onClick={step.clickable ? step.onClick : undefined}
                className={
                  step.attentionPulse
                    ? 'wingit-logbook-reminder'
                    : undefined
                }
                style={{
                  ...styles.step,
                  borderColor: tone.border,
                  background: tone.surface,
                  opacity: step.tone === 'locked' ? 0.55 : 1,
                  cursor: step.clickable ? 'pointer' : 'default',
                  animation: step.attentionPulse
                    ? 'wingitLogbookReminderPulse 2.4s ease-in-out infinite'
                    : 'none',
                  transformOrigin: 'center',
                }}
              >
                <div
                  style={{
                    ...styles.marker,
                    color: tone.markerText,
                    borderColor: tone.markerBorder,
                    background: tone.markerBackground,
                    boxShadow: tone.glow,
                  }}
                >
                  {tone.symbol}
                </div>

                <div
                  style={{
                    ...styles.iconFrame,
                    color: tone.color,
                    borderColor: tone.border,
                    background: tone.background,
                  }}
                >
                  <Icon size={isMobile ? 18 : 20} strokeWidth={2.1} />
                </div>

                <div style={styles.copy}>
                  <div style={styles.stepLabel}>{step.label}</div>
                  <div
                    style={{
                      ...styles.status,
                      color: tone.color,
                    }}
                  >
                    {step.status}
                  </div>

                  {step.helperLine ? (
                    <div style={styles.helperLine}>
                      {step.helperLine}
                    </div>
                  ) : null}

                  {step.secondaryAction ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        step.secondaryAction.onClick();
                      }}
                      style={styles.secondaryActionButton}
                    >
                      {step.secondaryAction.label}
                    </button>
                  ) : null}
                </div>
              </Wrapper>

              {!isMobile && index < steps.length - 1 && (
                <div style={styles.connector}>
                  <span
                    style={{
                      ...styles.connectorLine,
                      background: connectorTone.background,
                      boxShadow: connectorTone.glow,
                      opacity: connectorTone.opacity,
                    }}
                  />
                  
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
}

function getConnectorTone(step, nextStep) {
  if (nextStep?.attentionPulse) {
    return {
      background: 'linear-gradient(90deg, rgba(85,199,255,0.18), rgba(85,199,255,0.82))',
      arrow: '#55c7ff',
      glow: '0 0 16px rgba(85,199,255,0.24)',
      opacity: 1,
    };
  }

  if (nextStep?.tone === 'active') {
    return {
      background: 'linear-gradient(90deg, rgba(55,214,122,0.75), rgba(85,199,255,0.95))',
      arrow: '#55c7ff',
      glow: '0 0 16px rgba(85,199,255,0.28)',
      opacity: 1,
    };
  }

  if (step?.tone === 'complete') {
    return {
      background: 'linear-gradient(90deg, rgba(55,214,122,0.78), rgba(55,214,122,0.38))',
      arrow: '#37d67a',
      glow: '0 0 12px rgba(55,214,122,0.18)',
      opacity: 0.95,
    };
  }

  return {
    background: 'linear-gradient(90deg, rgba(150,176,207,0.20), rgba(150,176,207,0.08))',
    arrow: '#70849d',
    glow: 'none',
    opacity: 0.5,
  };
}

function getPreFlightStatus(
  preFlightProgressState,
  flowState
) {
  if (flowState === 'none') return 'Locked';

  if (flowState === 'landed') {
    if (preFlightProgressState === 'completed') {
      return 'COMPLETED';
    }

    if (preFlightProgressState === 'action_required') {
      return 'ACTION REQUIRED';
    }

    return 'Review only';
  }

  if (preFlightProgressState === 'completed') {
    return 'COMPLETED';
  }

  if (preFlightProgressState === 'action_required') {
    return 'ACTION REQUIRED';
  }

  return 'Next step';
}

function getPreFlightTone(
  preFlightProgressState,
  flowState
) {
  if (flowState === 'none') return 'locked';

  if (flowState === 'landed') {
    if (preFlightProgressState === 'completed') {
      return 'complete';
    }

    if (preFlightProgressState === 'action_required') {
      return 'warning';
    }

    return 'quiet';
  }

  if (preFlightProgressState === 'completed') {
    return 'complete';
  }

  if (preFlightProgressState === 'action_required') {
    return 'warning';
  }

  return 'active';
}
function getPostFlightStatus(stage, flowState) {
  if (stage?.status === 'complete') return 'COMPLETED';
  if (stage?.status === 'current') return 'Next step';
  if (flowState === 'none' || flowState === 'booked') return 'Waiting';
  if (flowState === 'in_progress') return 'After landing';
  return 'Waiting';
}

function getPostFlightTone(stage, flowState) {
  if (stage?.status === 'complete') return 'complete';
  if (stage?.status === 'current') return 'active';
  if (flowState === 'none') return 'locked';
  return 'quiet';
}

function getLogbookStatus(stage) {
  if (stage?.status === 'complete') return 'COMPLETED';
  if (stage?.status === 'current') return 'COMPLETE LOGBOOK';
    return 'Locked';
}

function getLogbookTone(stage) {
  if (stage?.status === 'complete') return 'complete';
  if (stage?.status === 'current') return 'active';
  return 'quiet';
}

function getTone(tone) {
  const ds = wingitDesign;

  if (tone === 'complete') {
    return {
      color: ds.colors.success,
      border: 'rgba(55,214,122,0.34)',
      background: 'rgba(55,214,122,0.09)',
      surface: 'rgba(10,22,36,0.50)',
      markerText: '#07111f',
      markerBorder: 'rgba(55,214,122,0.75)',
      markerBackground: ds.colors.success,
      glow: '0 0 18px rgba(55,214,122,0.34)',
      symbol: '✓',
    };
  }

  if (tone === 'active') {
    return {
      color: ds.colors.primary,
      border: 'rgba(85,199,255,0.38)',
      background: 'rgba(85,199,255,0.10)',
      surface: 'rgba(10,22,36,0.62)',
      markerText: '#07111f',
      markerBorder: 'rgba(85,199,255,0.78)',
      markerBackground: ds.colors.primary,
      glow: '0 0 18px rgba(85,199,255,0.36)',
      symbol: '▶',
    };
  }

  if (tone === 'warning') {
    return {
      color: '#f2b84b',
      border: 'rgba(242,184,75,0.40)',
      background: 'rgba(242,184,75,0.12)',
      surface: 'rgba(20,22,30,0.58)',
      markerText: '#241503',
      markerBorder: 'rgba(242,184,75,0.82)',
      markerBackground: '#f2b84b',
      glow: '0 0 18px rgba(242,184,75,0.34)',
      symbol: '!',
    };
  }

  if (tone === 'locked') {
    return {
      color: '#7d91aa',
      border: 'rgba(150,176,207,0.16)',
      background: 'rgba(150,176,207,0.045)',
      surface: 'rgba(10,22,36,0.28)',
      markerText: '#7d91aa',
      markerBorder: 'rgba(150,176,207,0.20)',
      markerBackground: 'rgba(150,176,207,0.04)',
      glow: 'none',
      symbol: '○',
    };
  }

  return {
    color: '#9fb6d1',
    border: 'rgba(150,176,207,0.20)',
    background: 'rgba(150,176,207,0.06)',
    surface: 'rgba(10,22,36,0.42)',
    markerText: '#9fb6d1',
    markerBorder: 'rgba(150,176,207,0.24)',
    markerBackground: 'rgba(150,176,207,0.06)',
    glow: 'none',
    symbol: '○',
  };
}

function getStyles(isMobile) {
  const ds = wingitDesign;

  return {
    panel: {
      ...ds.cards.primary,
      gridColumn: '1 / -1',
      borderRadius: isMobile ? ds.radius.large : ds.radius.xl,
      padding: isMobile ? '0.85rem' : '1rem',
      overflow: 'hidden',
    },

    header: {
      marginBottom: isMobile ? '0.65rem' : '0.75rem',
    },

    title: {
      margin: 0,
      color: ds.colors.text,
      fontSize: isMobile ? '1.08rem' : '1.28rem',
      fontWeight: 850,
      letterSpacing: '-0.03em',
    },

    subtitle: {
      margin: '0.25rem 0 0',
      color: ds.colors.textMuted,
      fontSize: isMobile ? '0.82rem' : '0.88rem',
      lineHeight: 1.35,
    },

    flow: {
      display: 'grid',
      gridTemplateColumns: isMobile
        ? '1fr'
        : 'minmax(0, 1fr) 24px minmax(0, 1fr) 24px minmax(0, 1fr) 24px minmax(0, 1fr)',
      gap: isMobile ? '0.5rem' : '0.3rem',
      alignItems: 'stretch',
    },

    step: {
      border: '1px solid',
      borderRadius: ds.radius.large,
      padding: isMobile ? '0.65rem' : '0.75rem',
      color: ds.colors.text,
      textAlign: 'left',
      display: 'grid',
      gridTemplateColumns: isMobile ? '24px 40px 1fr' : '1fr',
      gridTemplateRows: isMobile ? 'auto' : '24px 42px 1fr',
      gap: isMobile ? '0.45rem' : '0.5rem',
      alignItems: isMobile ? 'center' : 'stretch',
      width: '100%',
      minHeight: isMobile ? 72 : 112,
      transition: ds.transition.normal,
    },

    marker: {
      width: isMobile ? 30 : 32,
      height: isMobile ? 30 : 32,
      borderRadius: 999,
      border: '1px solid',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '0.72rem' : '0.74rem',
      fontWeight: 950,
      flexShrink: 0,
    },

    iconFrame: {
      width: isMobile ? 36 : 38,
      height: isMobile ? 36 : 38,
      borderRadius: 13,
      border: '1px solid',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      backdropFilter: 'blur(10px)',
      justifySelf: isMobile ? 'start' : 'end',
    },

    copy: {
      minWidth: 0,
      alignSelf: 'end',
    },

    stepLabel: {
      color: ds.colors.text,
      fontSize: isMobile ? '0.9rem' : '0.95rem',
      fontWeight: 850,
      lineHeight: 1.15,
      marginBottom: '0.38rem',
    },

    status: {
      fontSize: '0.66rem',
      fontWeight: 900,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
    },

    helperLine: {
      marginTop: '0.3rem',
      color: ds.colors.textMuted,
      fontSize: '0.72rem',
      lineHeight: 1.3,
    },

    secondaryActionButton: {
      ...ds.buttons.secondary,
      marginTop: '0.45rem',
      borderRadius: 999,
      padding: '0.32rem 0.62rem',
      fontSize: '0.72rem',
      fontWeight: 800,
    },

    connector: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    connectorLine: {
      width: '100%',
      height: 3,
      borderRadius: 999,
      transition: ds.transition.normal,
    },

    
  };
}